import * as THREE from 'three'
import {
  buildWorkBufferCopySegments,
  type WorkBufferCopySegment,
} from '@/plugins/gsplat/lod/GSplatWorkBufferCopySegments'
import type { ExtendedWebGLContext } from '@/plugins/gsplat/types/WebGLTypes'
import { getLoggerManager } from '@/shared/logging'
import type { GSplatInfo } from './GSplatInfo'
import type { GSplatWorkBuffer } from './GSplatWorkBuffer'

const log = getLoggerManager().getLogger('gsplat:lod')

/**
 * 默认白色（当未提供 colorsByLod 时使用）
 */
const WHITE_COLOR = [1, 1, 1]

/** 渲染通道模式 */
type PassMode = 'full' | 'colorOnly' | 'scalarOnly'

/**
 * WorkBuffer 渲染通道
 *
 * 封装渲染多个 GSplatInfo 到 WorkBuffer 的逻辑
 *
 * @remarks
 * 核心职责：
 * 1. **过滤激活的 Splats**：跳过 activeSplats = 0 的对象
 * 2. **管理渲染状态**：设置 BlendState、CullMode、DepthState
 * 3. **执行批量渲染**：遍历 splats，逐个渲染到工作缓冲区
 * 4. **多模式支持**：
 *    - full：完整 MRT 渲染（颜色+几何）
 *    - colorOnly：仅颜色渲染（优化 SH 更新）
 *    - scalarOnly：仅标量渲染（用于 scalar filter）
 *
 * 对应 PlayCanvas 的 GSplatWorkBufferRenderPass
 */
export class GSplatWorkBufferRenderPass {
  /**
   * WebGL 渲染器
   */
  /* @internal */ private renderer: THREE.WebGLRenderer

  /**
   * 关联的工作缓冲区
   */
  /* @internal */ private workBuffer: GSplatWorkBuffer

  /**
   * 渲染通道模式
   */
  /* @internal */ private passMode: PassMode

  /**
   * 渲染目标
   */
  /* @internal */ private renderTarget: THREE.WebGLRenderTarget | null

  /**
   * 待渲染的 Splat 列表（过滤后的）
   */
  private splats: GSplatInfo[] = []

  /**
   * 相机节点
   */
  /* @internal */ private cameraNode: THREE.Object3D | null = null

  /**
   * 每个 LOD 的调试颜色
   */
  private colorsByLod?: number[][]

  /**
   * 视图矩阵（临时变量，避免重复分配）
   */
  /* @internal */ private viewMatrix: THREE.Matrix4 = new THREE.Matrix4()

  /**
   * 临时矩阵（避免频繁分配）
   */
  /* @internal */ private tempMatrix: THREE.Matrix4 = new THREE.Matrix4()

  /**
   * 创建渲染通道
   *
   * @param renderer - WebGL 渲染器
   * @param workBuffer - 关联的工作缓冲区
   * @param passMode - 渲染通道模式
   */
  constructor(
    renderer: THREE.WebGLRenderer,
    workBuffer: GSplatWorkBuffer,
    passMode: PassMode = 'full'
  ) {
    this.renderer = renderer
    this.workBuffer = workBuffer
    this.passMode = passMode

    // 选择渲染目标
    if (passMode === 'colorOnly') {
      this.renderTarget = workBuffer.colorRenderTarget
    } else if (passMode === 'scalarOnly') {
      this.renderTarget = workBuffer.scalarRenderTarget
    } else {
      this.renderTarget = workBuffer.renderTarget
    }
  }

  /** 向后兼容：是否为 colorOnly 模式 */
  get colorOnly(): boolean {
    return this.passMode === 'colorOnly'
  }

  /** 是否为 scalarOnly 模式 */
  get scalarOnly(): boolean {
    return this.passMode === 'scalarOnly'
  }

  /**
   * 更新渲染参数
   *
   * @param splats - 所有 GSplatInfo 对象
   * @param cameraNode - 相机节点
   * @param colorsByLod - 每个 LOD 的颜色（可选）
   * @returns 如果有需要渲染的 splats 返回 true
   *
   * @remarks
   * 过滤逻辑：只保留 activeSplats > 0 的对象
   */
  update(splats: GSplatInfo[], cameraNode: THREE.Object3D, colorsByLod?: number[][]): boolean {
    // 清空上一帧的数据
    this.splats.length = 0
    this.colorsByLod = colorsByLod
    this.cameraNode = cameraNode

    // 过滤激活的 splats
    for (let i = 0; i < splats.length; i++) {
      const splatInfo = splats[i]
      if (splatInfo.activeSplats > 0) {
        this.splats.push(splatInfo)
      }
    }

    return this.splats.length > 0
  }

  /**
   * 执行渲染
   *
   * @remarks
   * 渲染流程：
   * 1. 保存当前渲染目标
   * 2. 设置 WorkBuffer 渲染目标
   * 3. 配置渲染状态（禁用混合、深度、剔除）
   * 4. 计算视图矩阵
   * 5. 遍历每个 splat，调用 renderSplat()
   * 6. 恢复渲染目标
   */
  render(): void {
    if (this.splats.length === 0) {
      return
    }

    // 添加 WebGL 调试分组（Spector.js 可识别）
    const gl = this.renderer.getContext() as ExtendedWebGLContext
    const hasDebugGroup = typeof gl.pushDebugGroup === 'function'

    if (hasDebugGroup && gl.pushDebugGroup && gl.DEBUG_SOURCE_APPLICATION !== undefined) {
      gl.pushDebugGroup(
        gl.DEBUG_SOURCE_APPLICATION,
        0,
        `GSplatWorkBufferRenderPass [passMode=${this.passMode}, splats=${this.splats.length}]`
      )
    }

    // 1. 保存当前渲染目标
    const currentRenderTarget = this.renderer.getRenderTarget()

    // 2. 设置渲染目标（scalarOnly 模式延迟获取，因为资源可能在构造后创建）
    const rt =
      this.passMode === 'scalarOnly' ? this.workBuffer.scalarRenderTarget : this.renderTarget
    if (!rt) {
      if (hasDebugGroup && gl.popDebugGroup) gl.popDebugGroup()
      this.renderer.setRenderTarget(currentRenderTarget)
      return
    }
    this.renderer.setRenderTarget(rt)

    // 3. 计算视图矩阵
    if (!this.cameraNode) {
      log.error('GSplatWorkBufferRenderPass: cameraNode is null')
      this.renderer.setRenderTarget(currentRenderTarget)
      if (hasDebugGroup && gl.popDebugGroup) {
        gl.popDebugGroup()
      }
      return
    }

    // 禁用 autoClear：此方法从 GSplatManager.update() 调用，运行在 pipeline.execute() 之外，
    // 不能依赖管线全局设置；Three.js renderer.render() 会重置 viewport 到 RT 全尺寸再 clear，
    // 导致后续 splat 的渲染清除前面已写入的数据
    const savedAutoClear = this.renderer.autoClear
    this.renderer.autoClear = false
    let colorAttachmentMaskApplied = false

    try {
      if (this.passMode === 'colorOnly') {
        // color-only 复用主 MRT，但只允许写 attachment 0，避免未声明的 shader outputs
        // 污染几何附件。finally 在仍绑定该 FBO 时恢复真实 GL 状态，与 Three 缓存保持一致。
        gl.drawBuffers([gl.COLOR_ATTACHMENT0, gl.NONE, gl.NONE])
        colorAttachmentMaskApplied = true
      }

      if (this.splats.some((splat) => (splat.resource?.shBands || 0) > 0)) {
        this.cameraNode.updateMatrixWorld(true)
        this.viewMatrix.copy(this.cameraNode.matrixWorld).invert()
      }

      // 4. 渲染每个 splat
      for (let i = 0; i < this.splats.length; i++) {
        this.renderSplat(this.splats[i])
      }
    } finally {
      // 5. 恢复状态（即使 renderSplat/configureMaterial 抛异常也确保恢复）
      if (colorAttachmentMaskApplied) {
        gl.drawBuffers([gl.COLOR_ATTACHMENT0, gl.COLOR_ATTACHMENT1, gl.COLOR_ATTACHMENT2])
      }
      this.renderer.autoClear = savedAutoClear
      this.renderer.setRenderTarget(currentRenderTarget)
      if (hasDebugGroup && gl.popDebugGroup) {
        gl.popDebugGroup()
      }
    }
  }

  /**
   * 渲染单个 Splat
   *
   * @param splatInfo - 要渲染的 Splat 信息
   *
   * @remarks
   * 渲染步骤：
   * 1. 获取或创建 WorkBufferRenderInfo（从 WorkBuffer 缓存）
   * 2. 设置材质参数：
   *    - 资源纹理（颜色、几何、SH、区间纹理等）
   * 3. 设置 Uniforms：
   *    - uActiveSplats：激活的 splat 数量
   *    - uStartLine：起始行
   *    - uViewportWidth：纹理宽度
   *    - uColorMultiply：LOD 调试颜色
   *    - matrix_view：资产根局部空间到视图空间的矩阵（仅用于 SH）
   * 4. 渲染全屏 quad
   */
  /* @internal */ private renderSplat(splatInfo: GSplatInfo): void {
    const { resource, intervals, activeSplats, lineStart, viewport } = splatInfo

    // 已释放 ImageBitmap 的 LOD 资源在 context restore 期间会串行重传源纹理。
    // 旧 WorldState 可能仍短暂引用该资源，恢复完成前必须跳过以避免绑定黑纹理。
    if ('gpuReady' in resource && resource.gpuReady === false) {
      return
    }

    // 获取 SH bands（从 resource 获取）
    const shBands = resource.shBands || 0

    // 检测是否为 SOGS 格式
    // 方式1: 检查构造函数名称
    // 方式2: 检查是否实现 IGSplatSogsResource 接口（有 meta 属性）
    const isSogs = resource.constructor.name === 'GSplatSogsResource' || 'meta' in resource

    // 1. 始终从 workBuffer 获取 WorkBufferRenderInfo
    // 注意：不使用 resource.getWorkBufferRenderInfo()，因为它返回的材质类型不同
    const modifier = splatInfo.placement.workBufferModifier
    const renderInfo = this.workBuffer.getWorkBufferRenderInfo(
      intervals.length > 0,
      this.passMode,
      shBands,
      isSogs, // 传递格式类型
      modifier
    )

    // 2. 设置材质 uniforms
    const material = renderInfo.material
    material.uniforms.uActiveSplats.value = activeSplats
    material.uniforms.uStartLine.value = lineStart
    material.uniforms.uViewportWidth.value = viewport.z
    material.uniforms.uColorMultiply.value.set(...this.getColorForLod(splatInfo.lodIndex))
    // WorkBuffer 永久保存 asset-local 数据。资产根世界变换只参与当前相机下的
    // SH 方向计算，不得烘焙进 center / rotation / scale 输出。
    if (shBands > 0) {
      splatInfo.node.updateMatrixWorld(true)
      this.tempMatrix.multiplyMatrices(this.viewMatrix, splatInfo.node.matrixWorld)
      material.uniforms.matrix_view.value.copy(this.tempMatrix)
    }

    // 防御：如果 viewport 为空，跳过渲染，避免 NaN/GL 错误
    if (viewport.z === 0 || viewport.w === 0) {
      return
    }

    // 3. 绑定资源纹理
    // 优先从 geometry 获取（单文件 GaussianSplatMesh），否则需要从 resource 配置
    const geometry = splatInfo.geometry
    if (geometry) {
      // 单文件模式：从 GaussianSplatGeometry 获取纹理
      material.uniforms.splatColor.value = geometry.colorTexture
      material.uniforms.splatDataA.value = geometry.dataTextureA
      material.uniforms.splatDataB.value = geometry.dataTextureB

      // SH 纹理（如果有）
      if (shBands > 0 && geometry.shTextures.length > 0) {
        material.uniforms.splatSH0.value = geometry.shTextures[0] || null
        if (shBands >= 2 && material.uniforms.splatSH1) {
          material.uniforms.splatSH1.value = geometry.shTextures[1] || null
        }
        if (shBands >= 3 && material.uniforms.splatSH2) {
          material.uniforms.splatSH2.value = geometry.shTextures[2] || null
        }
      }
    } else {
      // Octree LOD 模式：需要从 resource 配置纹理
      // 检查 resource 是否有 configureMaterial 方法（IGSplatSogsResource）
      if ('configureMaterial' in resource && typeof resource.configureMaterial === 'function') {
        // 调用 resource 的 configureMaterial 来设置纹理
        resource.configureMaterial(material)
      } else {
        return
      }
    }

    // 4. scalarOnly 模式：设置标量计算模式
    if (this.passMode === 'scalarOnly' && material.uniforms.uScalarMode) {
      material.uniforms.uScalarMode.value = this.workBuffer.scalarMode
      material.uniforms.uScalarFilterExempt.value = splatInfo.placement.isEnvironment
    }

    const copySegments = this.buildCopySegments(splatInfo, activeSplats, viewport.z, lineStart)
    this.renderCopySegments(material, renderInfo.quadRender, copySegments)
  }

  private buildCopySegments(
    splatInfo: GSplatInfo,
    activeSplats: number,
    textureWidth: number,
    lineStart: number
  ): WorkBufferCopySegment[] {
    if (splatInfo.intervals.length === 0) {
      return buildWorkBufferCopySegments({
        activeSplats,
        textureWidth,
        lineStart,
        includeTrailingPadding: true,
      })
    }

    const segments: WorkBufferCopySegment[] = []
    let targetOffset = 0

    for (let i = 0; i < splatInfo.intervals.length; i += 2) {
      const sourceBase = splatInfo.intervals[i]
      const count = splatInfo.intervals[i + 1] - sourceBase

      if (count <= 0) {
        continue
      }

      segments.push(
        ...buildWorkBufferCopySegments({
          activeSplats: count,
          textureWidth,
          lineStart,
          targetOffset,
          sourceBase,
        })
      )
      targetOffset += count
    }

    return segments
  }

  private renderCopySegments(
    material: THREE.ShaderMaterial,
    quadRender: {
      render(renderer: THREE.WebGLRenderer): void
      renderSegments?(
        renderer: THREE.WebGLRenderer,
        segments: readonly WorkBufferCopySegment[],
        targetSize: number
      ): void
    },
    segments: WorkBufferCopySegment[]
  ): void {
    if (segments.length === 0) {
      return
    }

    const renderer = this.renderer
    if (quadRender.renderSegments) {
      for (const segment of segments) {
        this.configureCopySegmentUniforms(material, segment)
        quadRender.renderSegments(renderer, [segment], this.workBuffer.textureSize)
      }
      return
    }

    const gl = renderer.getContext()
    const savedViewport = gl.getParameter(gl.VIEWPORT) as Int32Array
    const savedScissor = gl.getParameter(gl.SCISSOR_BOX) as Int32Array
    const savedScissorTest = gl.isEnabled(gl.SCISSOR_TEST)

    try {
      gl.enable(gl.SCISSOR_TEST)

      for (const segment of segments) {
        this.configureCopySegmentUniforms(material, segment)

        // WorkBuffer render targets use texture texel coordinates. Three.js public
        // viewport/scissor APIs multiply by devicePixelRatio, which breaks mobile DPR > 1.
        gl.viewport(segment.x, segment.y, segment.width, segment.height)
        gl.scissor(segment.x, segment.y, segment.width, segment.height)
        quadRender.render(renderer)
      }
    } finally {
      gl.viewport(savedViewport[0], savedViewport[1], savedViewport[2], savedViewport[3])
      gl.scissor(savedScissor[0], savedScissor[1], savedScissor[2], savedScissor[3])
      if (savedScissorTest) {
        gl.enable(gl.SCISSOR_TEST)
      } else {
        gl.disable(gl.SCISSOR_TEST)
      }
    }
  }

  private configureCopySegmentUniforms(
    material: THREE.ShaderMaterial,
    segment: WorkBufferCopySegment
  ): void {
    material.uniforms.uSegmentSourceBase.value = segment.sourceBase
    material.uniforms.uSegmentTargetOffset.value = segment.targetOffset
    material.uniforms.uSegmentCount.value = segment.count
    material.uniforms.uSegmentX.value = segment.x
    material.uniforms.uSegmentY.value = segment.y
    material.uniforms.uSegmentWidth.value = segment.width
  }

  /**
   * 获取指定 LOD 的调试颜色
   *
   * @param lodIndex - LOD 索引
   * @returns RGB 颜色数组
   *
   * @remarks
   * 回退策略：
   * 1. 使用 colorsByLod[lodIndex]
   * 2. 如果不存在，使用 colorsByLod[0]
   * 3. 如果都没有，使用白色
   */
  /* @internal */ private getColorForLod(_lodIndex: number): number[] {
    if (!this.colorsByLod) {
      return WHITE_COLOR
    }

    return this.colorsByLod[_lodIndex] ?? this.colorsByLod[0] ?? WHITE_COLOR
  }

  /**
   * 销毁渲染通道
   */
  destroy(): void {
    this.splats.length = 0
    this.cameraNode = null
    this.colorsByLod = undefined
  }
}
